import { defineStore } from "pinia";
import { handleError } from "@/helpers/helpers";
import { useSessionStore } from "@/stores/sessionStore";
import RepositoryFactory from "@/repositories/apiGateWay/repositoryFactory";
const apiGatewayRepository = RepositoryFactory.get('apiGateway');
import Logger from "@/helpers/logger";
import ApplicationError from "@/helpers/applicationError";
import { COUNTRY_CODE_JAPAN, HTTP_CODE_BAD_REQUEST, API_RESPONSE_SUCCESS, BAD_REQUEST_ERROR_CODE } from "@/data/constants";
import router from "@/router";

export const useUserStore = defineStore("users", {
    id: "user",
    state: () => {
        return {
            isLoading: false,
            registrationParams: {
                email: "",
                password: "",
                password_confirmation: "",
                first_name: "",
                last_name: "",
                first_name_kana: "",
                last_name_kana: "",
                zip_code: "",
                country: "",
                address_1: "",
                address_2: "",
                address_3: "",
                tel: "",
                year: "",
                month: "",
                day: "",
                sex: 1,
            },
            registrationErrorMessage: "",
            accountUpdateParams: {
                email: null,
                first_name: null,
                last_name: null,
                first_name_kana: null,
                last_name_kana: null,
                zip_code: null,
                country: null,
                address_1: null,
                address_2: null,
                address_3: null,
                tel: null,
                year: null,
                month: null,
                day: null,
                sex: null,
            },
            updateErrorMessage: "",
            registerVerifyParams: {
                email: null,
                password: null,
                password_confirmation: null,
            },
            registerVerifyErrorMsg: null,
            registerVerifyErrors: {},
            editAccountParams: {
                country: null,
                last_name: null,
                first_name: null,
                full_name: null,
                last_name_kana: null,
                first_name_kana: null,
                birthday: null,
                sex: null,
                tel: null
            },
            isOverseasUser: null,
            editAccountErrors: {},
            editAccountParamsErrorMsg: null,
            emailChangeRequestParams: {
                email: null,
            },
            emailChangeRequestErrorMsg: null,
            emailChangeRequestErrors: {},
            emailChangeParams: {
                code_1: "",
                code_2: "",
                code_3: "",
                code_4: "",
                code_5: "",
                code_6: "",
            },
            emailChangeErrorMsg: null,
            emailChangeErrors: {},
            canDeleteAccount: false,
            countries: [],
            activeMerchants: [],
        };
    },
    persist: {
        storage: sessionStorage,
        paths: ["editAccountParams"],
    },
    getters: {
        getAccountUpdateParams(state) {
            return state.accountUpdateParams;
        },
        getRegisterVerifyParams(state) {
            return state.registerVerifyParams;
        },
        getEditAccountParams(state) {
            return state.editAccountParams;
        },
        getEmailChangeRequestParams(state) {
            return state.emailChangeRequestParams;
        },
        getEmailChangeParams(state) {
            let p = state.emailChangeParams
            p.auth_code = p.code_1.toString() + p.code_2.toString() + p.code_3.toString() + p.code_4.toString() + p.code_5.toString() + p.code_6.toString();
            return p;
        },
    },
    actions: {
        resetEmailChangeErrorMessages() {
            this.emailChangeErrors = {};
            this.emailChangeErrorMsg = null;
        },
        resetErrorMessages() {
            this.registrationError = false;
        },
        async getCountries() {
            try {
                const response = await apiGatewayRepository.getCountries();
                if (response.data != null && response.success == API_RESPONSE_SUCCESS) {
                    this.countries = response.data;
                    this.countries = this.countries.filter(country => country.id !== COUNTRY_CODE_JAPAN); // remove Japan from the list
                    this.countries.sort((a, b) => {
                        return a.name.localeCompare(b.name);
                    });
                }
            } catch (e) {
                throw new ApplicationError(e.message, {}, e)
            }
        },
        async getAccountInfo() {
            try {
                this.isLoading = true;
                let sessionData = this.getSessionIdAndAuthId();
                apiGatewayRepository.ssid = sessionData.HVCHUBSYNCHSSID;
                apiGatewayRepository.auth_id = sessionData.auth_id;
                const result = await apiGatewayRepository.getAccountInfo();

                if (result.data.success === 1) {
                    const d = result.data.data;
                    // extract date
                    this.editAccountParams.first_name = d.first_name;
                    this.editAccountParams.last_name = d.last_name;
                    this.editAccountParams.first_name_kana = d.first_name_kana;
                    this.editAccountParams.last_name_kana = d.last_name_kana;
                    this.editAccountParams.tel = d.tel;
                    this.editAccountParams.birthday = d.birthday;
                    this.editAccountParams.sex = d.sex_code;
                    this.editAccountParams.email = d.email;
                    this.editAccountParams.zip_code = d.zip_code;
                    this.editAccountParams.address_1 = d.address_1_code;
                    this.editAccountParams.address_1_name = d.address_1;
                    this.editAccountParams.address_2 = d.address_2;
                    this.editAccountParams.address_3 = d.address_3;
                    this.editAccountParams.country = d.country_code;
                    if (d.country_code === COUNTRY_CODE_JAPAN) {
                        this.isOverseasUser = false;
                    } else {
                        this.isOverseasUser = true;
                        this.editAccountParams.full_name = d.first_name; // API側でfull_nameを返却するようになるまではfirst_nameにフルネームが入る想定
                    }

                    this.isLoading = false;
                    return d;
                }
                this.isLoading = false;
            } catch (e) {
                this.isLoading = false;
                throw new ApplicationError(e);
            }
        },
        async isValidUserInfo() {
            try {
                const accountInfo = await this.getAccountInfo();

                if (accountInfo.country_code === COUNTRY_CODE_JAPAN) {
                    return this.isValidUserInfoForJapan(accountInfo);
                } else {
                    return this.isValidUserInfoForOverseas(accountInfo);
                }
            } catch (error) {
                useSessionStore().clearSession();
                return true;
            }
        },
        isValidUserInfoForJapan(accountInfo) {
            if (accountInfo.first_name === null || accountInfo.first_name === "" ||
                accountInfo.last_name === null || accountInfo.last_name === "" ||
                accountInfo.first_name_kana === null || accountInfo.first_name_kana === "" ||
                accountInfo.last_name_kana === null || accountInfo.last_name_kana === "" ||
                accountInfo.birthday === null || accountInfo.birthday === "" ||
                accountInfo.sex === null || accountInfo.sex === "" ||
                accountInfo.tel === null || accountInfo.tel === "") {
                return false;
            } else {
                return true;
            }

        },
        isValidUserInfoForOverseas(accountInfo) {
            if (accountInfo.first_name === null || accountInfo.first_name === "" ||
                accountInfo.birthday === null || accountInfo.birthday === "" ||
                accountInfo.sex === null || accountInfo.sex === "" ||
                accountInfo.tel === null || accountInfo.tel === "") {
                return false;
            } else {
                return true;
            }

        },
        async registerUser() {
            try {
                this.isLoading = true;

                this.resetErrorMessages();
                this.registrationParams.address_1 = this.registrationParams.address_1.toString();
                this.registrationParams.birthday = `${this.registrationParams.year}-${this.registrationParams.month}-${this.registrationParams.day}`;

                const response = await apiGatewayRepository.register(this.registrationParams);

                if (response.data == null || response.success != 1) {
                    this.isLoading = false;
                    return false;
                }

                this.isLoading = false;
                return true;
            } catch (error) {
                Logger.captureMessage(error.message)
                this.registrationError = true;
                this.isLoading = false;
                this.registrationError = true;
                const errorMessage = await handleError(error);
                if (errorMessage != null) {
                    this.registrationErrorMessage = errorMessage;
                }
                return false;
            }
        },
        async registerVerify() {
            try {
                this.isLoading = true;
                this.registerVerifyErrorMsg = null;

                await apiGatewayRepository.register(this.getRegisterVerifyParams);

                this.isLoading = false;

                await router.push({ name: "register.verify.completed" });
            } catch (error) {
                Logger.captureMessage(error.message)
                this.isLoading = false;

                if (error.response.data && error.response.data.errors) {
                    const data = error.response.data;

                    if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "object") {
                        this.registerVerifyErrors = data.errors;
                    }

                    if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "string") {
                        this.registerVerifyErrorMsg = data.errors;
                    }
                    return false;
                }

                throw new ApplicationError(error.message, this.getRegisterVerifyParams, error);
            }
        },

        async editAccountConfirm() {
            try {
                this.isLoading = true;

                const params = {
                    "last_name": this.getEditAccountParams.last_name,
                    "first_name": this.getEditAccountParams.first_name,
                    "last_name_kana": this.getEditAccountParams.last_name_kana,
                    "first_name_kana": this.getEditAccountParams.first_name_kana,
                    "birthday": this.getEditAccountParams.birthday,
                    "sex": parseInt(this.getEditAccountParams.sex),
                    "tel": this.getEditAccountParams.tel,
                    "zip_code": this.getEditAccountParams.zip_code,
                    "address_1": String(this.getEditAccountParams.address_1),
                    "address_2": this.getEditAccountParams.address_2,
                    "address_3": this.getEditAccountParams.address_3,
                };

                if (this.isOverseasUser) {
                    params.country = String(this.getEditAccountParams.country);
                    params.full_name = this.getEditAccountParams.full_name;
                    delete params.first_name;
                    delete params.last_name;
                    delete params.last_name_kana;
                    delete params.first_name_kana;
                    delete params.address_1;
                } else {
                    params.country = String(COUNTRY_CODE_JAPAN);
                }

                await apiGatewayRepository.updateAccountInfo(params)
                    .then(() => {
                        router.push({ name: "edit.account.finish" });
                    })
                    .catch(e => {
                        const data = e.response.data;

                        if (data.code === HTTP_CODE_BAD_REQUEST && typeof data.errors === "object") {
                            this.editAccountErrors = data.errors;
                            this.isLoading = false;
                            router.push({ name: "edit.account" })
                            return false;
                        }

                        if (data.code === HTTP_CODE_BAD_REQUEST && typeof data.errors == "string") {
                            this.editAccountParamsErrorMsg = data.errors;
                            this.isLoading = false;
                            router.push({ name: "edit.account" })
                            return false;
                        }

                        this.isLoading = false;
                        throw new ApplicationError();
                    });

                this.isLoading = false;
            } catch (e) {
                throw new ApplicationError(e);
            }
        },

        async updateAccountInfo() {
            try {
                const params = {
                    zip_code: this.editAccountParams.zip_code,
                    address_1: this.editAccountParams.address_1 ? this.editAccountParams.address_1.toString() : null,
                    address_2: this.editAccountParams.address_2,
                    address_3: this.editAccountParams.address_3,
                    tel: this.editAccountParams.tel,
                };

                if (this.isOverseasUser) {
                    params.country = String(this.getEditAccountParams.country);
                } else {
                    params.country = String(COUNTRY_CODE_JAPAN);
                }

                await apiGatewayRepository.updateAccountInfo(params)
                    .then(() => {
                        return true;
                    })
                    .catch(e => {
                        const data = e.response.data;

                        if (data.code === HTTP_CODE_BAD_REQUEST && typeof data.errors === "object") {
                            this.editAccountErrors = data.errors;
                            router.push({ name: "user.profile" })
                            return false;
                        }

                        if (data.code === HTTP_CODE_BAD_REQUEST && typeof data.errors == "string") {
                            this.editAccountParamsErrorMsg = data.errors;
                            router.push({ name: "user.profile" })
                            return false;
                        }

                        throw new ApplicationError();
                    });
            } catch (e) {
                throw new ApplicationError(e);
            }
        },
        async deleteUser() {
            try {
                let sessionInfo = this.getSessionIdAndAuthId();
                const params = {
                    user_id: sessionInfo.hubsynch_id,
                    resign_from_all_apps: "true"
                };

                const response = await apiGatewayRepository.resign(params);
                if (response.data != null && response.success == API_RESPONSE_SUCCESS) {
                    useSessionStore().clearSession();
                    router.push({ name: "account.deleted" })
                }
            } catch (e) {
                throw new ApplicationError(e.message, {}, e)
            }
        },
        getSessionIdAndAuthId() {
            return {
                'HVCHUBSYNCHSSID': localStorage.getItem('HVCHUBSYNCHSSID'),
                'auth_id': localStorage.getItem('auth_id'),
                'hubsynch_id': localStorage.getItem('hubsynch_id')
            }
        },
        async sendAuthCode() {
            try {
                this.isLoading = true;

                await apiGatewayRepository.sendAuthCode(this.getEmailChangeRequestParams);

                this.isLoading = false;
                return true;
            } catch (error) {
                Logger.captureMessage(error.message)
                this.isLoading = false;

                if (error.response.data && error.response.data.errors) {
                    const data = error.response.data;
                    // if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "object") {
                    //     this.emailChangeRequestErrors = data.errors;
                    // }

                    // if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors.message === "string") {
                    //     this.emailChangeRequestErrorMsg = data.errors.message;
                    // }
                    if (
                        data.code === BAD_REQUEST_ERROR_CODE &&
                        typeof data.errors === "object" &&
                        String(data.error_code) === "1420088"
                    ) {
                        this.emailChangeRequestErrors = { message: "メールアドレスは既に使用されています。" };
                        this.emailChangeRequestErrorMsg = "メールアドレスは既に使用されています。";
                    } else {
                        this.emailChangeRequestErrors = { message: "メールアドレスの変更に失敗しました。時間をおいて再度お試しください。" };
                        this.emailChangeRequestErrorMsg = "メールアドレスの変更に失敗しました。時間をおいて再度お試しください。";
                    }

                    return false;
                }

                throw new ApplicationError(error.message, this.getEmailChangeRequestParams, error);
            }
        },

        async verifyAuthCode() {
            try {
                let p = {
                    auth_code: this.getEmailChangeParams.auth_code,
                }
                await apiGatewayRepository.verifyAuthCode(p);

                await this.getAccountInfo();

                return true;
            } catch (error) {
                Logger.captureMessage(error.message)

                if (error.response.data && error.response.data.errors) {
                    const data = error.response.data;
                    if (data.code === BAD_REQUEST_ERROR_CODE) {
                        if (typeof data.errors === "object") {
                            this.emailChangeErrors = data.errors;
                        }

                        if (typeof data.errors.message === "string") {
                            this.emailChangeErrorMsg = data.errors.message;
                        }
                    }
                    return false;
                }
                throw new ApplicationError(error.message, this.getEmailChangeParams, error);
            }
        },
        async getCanDeleteAccount() {
            try {
                const response = await apiGatewayRepository.getActiveMerchants();
                if (response.data != null && response.success == API_RESPONSE_SUCCESS) {
                    this.canDeleteAccount = response.data.length === 0; // if there are active merchants, user cannot delete account
                    this.activeMerchants = response.data.slice(0, 3);
                }
            } catch (e) {
                throw new ApplicationError(e.message, {}, e)
            }
        }

    },
});
